import { useState } from 'react';
import {
  Form,
  Input,
  Button,
  Select,
  Radio,
  DatePicker,
  Space,
  message,
} from 'antd';
import { SendOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  broadcastCreate,
  type BroadcastPriority,
  type BroadcastTargetType,
} from '@/services/broadcast';
import styles from './BroadcastAdmin.module.css';

interface FormValues {
  title: string;
  contentMd: string;
  priority: BroadcastPriority;
  targetType: BroadcastTargetType;
  targetUserIds?: string[];
  expireAt?: Dayjs | null;
}

interface Props {
  onPublished?: () => void;
}

function Publish({ onPublished }: Props) {
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [targetType, setTargetType] = useState<BroadcastTargetType>('all');

  const handleSubmit = async (values: FormValues) => {
    if (
      values.targetType === 'users' &&
      (!values.targetUserIds || values.targetUserIds.length === 0)
    ) {
      message.error('请至少选择一位接收人');
      return;
    }
    setSubmitting(true);
    try {
      await broadcastCreate({
        title: values.title.trim(),
        contentMd: values.contentMd,
        priority: values.priority,
        targetType: values.targetType,
        targetUserIds: values.targetUserIds,
        expireAt: values.expireAt ? values.expireAt.toISOString() : null,
      });
      message.success('广播已发出');
      form.resetFields();
      setPreviewContent('');
      setTargetType('all');
      onPublished?.();
    } catch (e) {
      // 拦截器已提示
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.panel}>
      <Form<FormValues>
        form={form}
        layout="vertical"
        initialValues={{ priority: 'normal', targetType: 'all' }}
        onFinish={handleSubmit}
        onValuesChange={(changed) => {
          if ('contentMd' in changed) setPreviewContent(changed.contentMd || '');
          if ('targetType' in changed) setTargetType(changed.targetType);
        }}
      >
        <Form.Item
          name="title"
          label="广播标题"
          rules={[{ required: true, message: '请输入标题' }, { max: 255 }]}
        >
          <Input placeholder="例如：星舰系统升级通告" size="large" />
        </Form.Item>

        <Form.Item
          name="priority"
          label="优先级"
          rules={[{ required: true }]}
        >
          <Radio.Group>
            <Radio value="normal">
              <span className={styles.priorityNormal}>普通</span>
            </Radio>
            <Radio value="important">
              <span className={styles.priorityImportant}>重要</span>
            </Radio>
            <Radio value="critical">
              <span className={styles.priorityCritical}>紧急</span>
            </Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="targetType"
          label="接收对象"
          rules={[{ required: true }]}
        >
          <Radio.Group>
            <Radio value="all">全员</Radio>
            <Radio value="users">指定用户</Radio>
          </Radio.Group>
        </Form.Item>

        {targetType === 'users' && (
          <Form.Item
            name="targetUserIds"
            label="接收人 ID 列表"
            tooltip="支持手动粘贴用户 ID，回车或逗号分隔；后续接入鉴权中心通讯录后可改为搜索勾选"
            rules={[
              {
                validator: (_, v) =>
                  Array.isArray(v) && v.length > 0
                    ? Promise.resolve()
                    : Promise.reject('请填写至少一个用户 ID'),
              },
            ]}
          >
            <Select
              mode="tags"
              placeholder="输入用户 ID 后回车添加"
              tokenSeparators={[',', ' ', '\n']}
              size="large"
            />
          </Form.Item>
        )}

        <Form.Item
          name="expireAt"
          label="过期时间"
          tooltip="过期后该广播不再出现在用户信箱与弹窗中。留空则长期有效。"
        >
          <DatePicker
            showTime
            format="YYYY-MM-DD HH:mm"
            disabledDate={(d) => d && d.isBefore(dayjs().startOf('day'))}
            placeholder="选择过期时间（可选）"
            style={{ width: 280 }}
          />
        </Form.Item>

        <Form.Item label="广播内容（Markdown）" required>
          <div className={styles.editorRow}>
            <Form.Item
              name="contentMd"
              noStyle
              rules={[{ required: true, message: '请输入广播内容' }]}
            >
              <Input.TextArea
                placeholder={`# 大标题\n\n正文支持 **加粗**、列表、代码块、表格等 Markdown 语法。`}
                autoSize={{ minRows: 14, maxRows: 26 }}
                style={{ fontFamily: 'monospace', fontSize: 13 }}
              />
            </Form.Item>
            <div className={styles.preview}>
              {previewContent ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {previewContent}
                </ReactMarkdown>
              ) : (
                <span className={styles.previewEmpty}>
                  实时预览将在此处显示
                </span>
              )}
            </div>
          </div>
        </Form.Item>

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SendOutlined />}
              loading={submitting}
              size="large"
            >
              发射广播
            </Button>
            <Button
              size="large"
              icon={<ReloadOutlined />}
              onClick={() => {
                form.resetFields();
                setPreviewContent('');
                setTargetType('all');
              }}
            >
              重置
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}

export default Publish;
